/**
 * Form schemas (04-architecture § 7). The frontends validate **forms**, not API
 * payloads — payload types come from `@cowoo/api-client`.
 *
 * Every message is an i18n key, never text.
 */

import { z } from 'zod';

export const emailSchema = z.string().min(1, 'validation.required').email('validation.email');

export const otpSchema = z
  .string()
  .regex(/^\d{6}$/, 'validation.tooShort')
  .describe('six digit e-mail code');

/**
 * OtterAuth's `authenticateTotp` takes 6 characters for an app code and 8 for a
 * recovery code (`llm.txt` § 4, TOTP), so the recovery path is only usable if
 * the input accepts both lengths — closing the last part of OI-F-07.
 */
export const totpSchema = z.string().regex(/^[0-9A-Za-z]{6}$|^[0-9A-Za-z]{8}$/, 'validation.tooShort');

export const newsletterSchema = z.object({
  email: emailSchema,
  list: z.enum(['loft_waitlist', 'general']),
  /** Honeypot — must stay empty (BR-NL-01). */
  hp: z.string().max(0).optional().default(''),
});
export type NewsletterForm = z.infer<typeof newsletterSchema>;

const dateOfBirth = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date')
  // A date in the future is a typo, not an age problem, and says so.
  .refine((day) => new Date(day) <= new Date(), 'validation.date')
  .refine((day) => isAtLeast18(day), 'validation.minAge');

/** BR-ACC-04 — 18 **today**, not "in the birth year"; the boundary is a birthday. */
export function isAtLeast18(day: string, now: Date = new Date()): boolean {
  const eighteen = new Date(now);
  eighteen.setFullYear(eighteen.getFullYear() - 18);
  return new Date(`${day}T00:00:00Z`) <= new Date(eighteen.toISOString().slice(0, 10) + 'T00:00:00Z');
}

/**
 * 06-mobile-app § 5.6 / 09-web-member § 5.3 — the one form (BR-ACC-03) behind
 * the membership funnel, the day-ticket funnel (D-063) and the profile edit.
 */
export const billingProfileSchema = z
  .object({
    kind: z.enum(['private', 'company']),
    firstName: z.string().trim().min(1, 'validation.required').max(80),
    lastName: z.string().trim().min(1, 'validation.required').max(80),
    dateOfBirth,
    // A street without a number is the single most common way an invoice
    // address turns out to be undeliverable.
    street: z.string().trim().min(3, 'validation.street').max(120).regex(/\d/, 'validation.street'),
    addressExtra: z.string().trim().max(80).optional(),
    postalCode: z.string().trim().min(3, 'validation.postalCode').max(10),
    city: z.string().trim().min(1, 'validation.required').max(80),
    country: z.string().length(2).toUpperCase(),
    companyName: z.string().trim().max(120).optional(),
    vatId: z
      .string()
      .trim()
      .transform((value) => value.toUpperCase().replace(/\s+/g, ''))
      .refine((value) => value === '' || /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(value), 'validation.vatId')
      .optional(),
  })
  .refine((v) => v.kind !== 'company' || Boolean(v.companyName), {
    message: 'validation.required',
    path: ['companyName'],
  })
  // German postal codes are exactly five digits; other countries vary too much
  // to guess, so only the length is checked there.
  .refine((v) => v.country !== 'DE' || /^\d{5}$/.test(v.postalCode), {
    message: 'validation.postalCode',
    path: ['postalCode'],
  });
export type BillingProfileForm = z.infer<typeof billingProfileSchema>;

export const enquirySchema = z.object({
  interest: z.enum(['office', 'private_desk', 'viewing', 'team']),
  company: z.string().max(120).optional(),
  persons: z.coerce.number().int().min(1).max(50).optional(),
  fromWhen: z.string().max(60).optional(),
  name: z.string().min(1, 'validation.required').max(120),
  email: emailSchema,
  phone: z.string().max(40).optional(),
  message: z.string().max(2000).optional(),
  hp: z.string().max(0).optional().default(''),
});
export type EnquiryForm = z.infer<typeof enquirySchema>;

/** § 312k cancellation form (BR-312K-01). */
export const cancellation312kSchema = z.object({
  kind: z.enum(['ordinary', 'extraordinary']),
  email: emailSchema,
  productKey: z.enum(['flex_desk', 'private_desk', 'office', 'locker']),
  fullName: z.string().min(1, 'validation.required').max(120),
  customerNumber: z.string().max(20).optional(),
  endDate: z.union([z.literal('next_possible'), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date')]),
  reasonText: z.string().max(2000).optional(),
  hp: z.string().max(0).optional().default(''),
});
export type Cancellation312kForm = z.infer<typeof cancellation312kSchema>;

/** Guest web check-in on `/p/:deskCode` (BR-GUEST-03). */
export const guestCheckinSchema = z.object({
  deskCode: z.string().length(6),
  ticketNumber: z.string().min(1, 'validation.required').max(24),
  checkinCode: z.string().length(6, 'validation.tooShort'),
});
export type GuestCheckinForm = z.infer<typeof guestCheckinSchema>;

/** Guest pass purchase in the add-on shop (BR-GUEST-01). */
export const guestPassSchema = z.object({
  guestName: z.string().min(1, 'validation.required').max(120),
  guestEmail: emailSchema,
  locationId: z.string().uuid(),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date'),
});
export type GuestPassForm = z.infer<typeof guestPassSchema>;

/**
 * Manual desk entry when the sticker QR will not scan (A-069).
 * The designed four digit boxes are wrong: labels are `14`, `O3`, `P2`.
 */
export const deskLabelSchema = z.object({
  locationId: z.string().uuid(),
  label: z.string().min(1, 'validation.required').max(12),
});
export type DeskLabelForm = z.infer<typeof deskLabelSchema>;
